import { type Mesh, MeshBuilder } from "@babylonjs/core";
import { HeroId, Slot, fxk, hv } from "@rivalwatch/shared";
import type { Cue } from "../../audio/engine.ts";
import * as loops from "../../audio/cues/loops.ts";
import { bell, click, fm, noise, thump, trim, whoosh } from "../../audio/synth.ts";
import type { ClientHero } from "../types.ts";

const V = { rage: 0, animal: 1, regen: 2, berserk: 3 } as const;
const FX = { Claw: fxk(HeroId.Woolverine, 0), Leap: fxk(HeroId.Woolverine, 1), Slam: fxk(HeroId.Woolverine, 2) } as const;
const clawCue: Cue = (o) => {
  click(o, 0.28, 4700, 0.003);
  whoosh(o, 0.35, 700, 3100, 0.15);
  fm(o, { f: 520, ratio: 3.1, index: 4, index1: 0, gain: 0.11, d: 0.14 });
  return 0.3;
};
const roarCue: Cue = (o) => {
  thump(o, 0.55, 92, 42, 0.22);
  noise(o, { color: "brown", type: "bandpass", f: 240, f1: 800, gain: 0.55, d: 0.45 });
  return 0.65;
};
const slamCue: Cue = (o) => {
  thump(o, 0.75, 80, 36, 0.22);
  noise(o, { color: "crackle", type: "bandpass", f: 1500, gain: 0.45, d: 0.28 });
  return 0.55;
};
const theme = {
  bpm: 118,
  octave: -1,
  voice: bell,
  notes: [
    [0, 0.5],
    [null, 0.25],
    [0, 0.25],
    [5, 0.5],
    [2, 1],
  ],
} satisfies ClientHero["audio"]["theme"];

type WoolGear = { aura: Mesh; claws: Mesh[]; sideburns: Mesh[] };

const hero: ClientHero<WoolGear> = {
  id: HeroId.Woolverine,
  bulk: 1.08,
  shape: { head: 0.95, torso: 1.05, arms: 1.12, legs: 0.95 },
  gear(g) {
    const { scene, rig, add, addWorld, glow, toon } = g;
    const metal = glow("#e6e9ef", 0.9);
    const blue = toon("#154a98");
    const hair = toon("#151515");
    const claws: Mesh[] = [];
    for (const hand of [rig.handL, rig.handR]) {
      for (const x of [-0.09, 0, 0.09]) {
        const c = add(MeshBuilder.CreateBox("wool-claw", { width: 0.028, height: 0.028, depth: 0.58 }, scene), hand, metal);
        c.position.set(x, -0.02, -0.34);
        claws.push(c);
      }
    }
    const sideburns: Mesh[] = [];
    for (const side of [-1, 1]) {
      const horn = add(MeshBuilder.CreateCylinder("swept-mask-horn", { diameterTop: 0.04, diameterBottom: 0.16, height: 0.38, tessellation: 4 }, scene), rig.head, blue);
      horn.position.set(side * 0.18, 0.21, -0.02);
      horn.rotation.z = side * 0.78;
      horn.rotation.x = -0.18;
      const burn = add(MeshBuilder.CreateBox("wool-sideburn", { width: 0.07, height: 0.24, depth: 0.04 }, scene), rig.head, hair);
      burn.position.set(side * 0.225, -0.06, -0.13);
      burn.rotation.z = side * 0.12;
      sideburns.push(burn);
    }
    const wool = add(MeshBuilder.CreateTorus("neck-wool", { diameter: 0.58, thickness: 0.08, tessellation: 18 }, scene), rig.torso, toon("#fff1c9"));
    wool.rotation.x = Math.PI / 2;
    wool.position.y = 0.2;
    const aura = addWorld(MeshBuilder.CreateSphere("rage-aura", { diameter: 1.6, segments: 12 }, scene), glow("#f5d000", 0.16));
    aura.setEnabled(false);
    return { aura, claws, sideburns };
  },
  animate(gear, f) {
    const rage = (f.v[V.rage] ?? 0) / 100;
    const clawScale = 1 + rage * 0.35 + ((f.v[V.berserk] ?? 0) > 0 ? 0.25 : 0);
    for (const claw of gear.claws) claw.scaling.z = clawScale;
    for (const burn of gear.sideburns) burn.scaling.y = 1 + rage * 0.25;
    gear.aura.setEnabled(rage > 0.3 || (f.v[V.animal] ?? 0) > 0 || f.ultActive);
    gear.aura.position.set(f.pos.x, f.pos.y + 0.85, f.pos.z);
    gear.aura.scaling.setAll(0.8 + rage * 0.8 + Math.sin(f.t * 10) * 0.04);
    if (f.moveKind >= 16) return "attack-melee-right";
    if (f.action === 5) return "attack-kick-right";
    if (f.action === 3 || f.action === 1) return "attack-melee-left";
    return "holding-both";
  },
  hud(c, eid) {
    return [
      { label: "Rage", value: hv(c, eid, V.rage) / 100, color: "#f5d000" },
      { label: "Animal", value: Math.min(1, hv(c, eid, V.animal) / (5 * 60)), color: "#ff8a3d" },
      { label: "Regen CD", value: 1 - Math.min(1, hv(c, eid, V.regen) / (90 * 60)), color: "#fff1c9" },
    ];
  },
  fx: {
    [FX.Claw]: { keepGeneric: true },
    [FX.Leap]: { keepGeneric: true },
    [FX.Slam]: { keepGeneric: true },
  },
  icons: {
    [Slot.Primary]: ["M5 19l4-14M12 19V5M19 19L15 5"],
    [Slot.Secondary]: ["M4 12h13", "M13 7l5 5-5 5", "M5 17l4-10"],
    [Slot.Ability1]: ["M4 18c4-8 9-12 16-12", "M16 5l4 2-2 4"],
    [Slot.Ability2]: ["M12 3l7 5v5c0 4-3 7-7 8-4-1-7-4-7-8V8z", "M8 13l4 4 4-8"],
    [Slot.Ultimate]: ["M5 18l7-14 7 14", "M7 14h10", "M4 21h16"],
    passive: ["M12 21c-4-3-6-6-6-10a6 6 0 0 1 12 0c0 4-2 7-6 10", "M9 12h6"],
  },
  audio: {
    weight: "mid",
    fire: () => ({ key: "fire:woolverine:claw", cue: trim(12, clawCue) }),
    ability: { [Slot.Secondary]: trim(12, clawCue), [Slot.Ability1]: trim(3, roarCue), [Slot.Ability2]: trim(3, roarCue), [Slot.Ultimate]: trim(1, slamCue) },
    theme,
    themeTrim: { heroic: 3.1, hostile: -6, tease: 10.2 },
    loops: ({ v, alive }) => (alive && v[V.animal] > 0 ? [{ key: "woolverine-animal", build: loops.breather, gain: 0.6 }] : []),
  },
};

export default hero;
